"use client";

import styles from "./explore-country.module.css";
import {
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdArrowForward,
  MdOutlineHome,
  MdOutlineMonetizationOn,
  MdOutlineStarBorder,
  MdOutlineAccessTime,
} from "react-icons/md";
import { Link } from "@/i18n/navigation";
import { useRef, useState } from "react";
import { makeLocalizedPublicHref } from "@/lib/localizedRoutes/routeHelpers";
import { useDomainContext } from "@/global-context/domain-context";
import { useLocale } from "next-intl";

const CITIES_DATA = [
  {
    id: "bucuresti",
    imageUrl: "/placeholder-images/city-living.png",
    title: "București",
    properties: "9,842 proprietăți",
    price: "De la €65,000",
    link: "/search?city=bucuresti",
  },
  {
    id: "cluj",
    imageUrl: "/placeholder-images/coastal-living.png",
    title: "Cluj-Napoca",
    properties: "2,341 proprietăți",
    price: "De la €72,000",
    link: "/search?city=cluj-napoca",
  },
  {
    id: "brasov",
    imageUrl: "/placeholder-images/countryside-living.png",
    title: "Brașov",
    properties: "1,287 proprietăți",
    price: "De la €58,000",
    link: "/search?city=brasov",
  },
  {
    id: "timisoara",
    imageUrl: "/placeholder-images/city-living.png",
    title: "Timișoara",
    properties: "1,156 proprietăți",
    price: "De la €55,000",
    link: "/search?city=timisoara",
  },
  {
    id: "constanta",
    imageUrl: "/placeholder-images/coastal-living.png",
    title: "Constanța",
    properties: "987 proprietăți",
    price: "De la €49,000",
    link: "/search?city=constanta",
  },
];

const TRENDS_DATA = [
  {
    id: 1,
    icon: MdOutlineHome,
    label: "Preț mediu București",
    value: "€1,650/m²",
    trend: "+4.3%",
    isPositive: true,
  },
  {
    id: 2,
    icon: MdOutlineMonetizationOn,
    label: "Randament închiriere (București)",
    value: "5.6%",
    trend: "+0.4%",
    isPositive: true,
  },
  {
    id: 3,
    icon: MdOutlineStarBorder,
    label: "Proprietăți noi în această lună",
    value: "1,248",
    trend: "+12.5%",
    isPositive: true,
  },
  {
    id: 4,
    icon: MdOutlineAccessTime,
    label: "Timp mediu de vânzare",
    value: "45 zile",
    trend: "-5 zile",
    isPositive: true,
  },
];

function CityCard({
  imageUrl,
  title,
  properties,
  price,
  link,
}: {
  imageUrl: string;
  title: string;
  properties: string;
  price: string;
  link: string;
}) {
  return (
    <div
      className={styles.cardContainer}
      style={{ backgroundImage: `url(${imageUrl})` }}
    >
      <div className={styles.cardOverlay}>
        <h3 className={styles.cardTitle}>{title}</h3>
        <p className={styles.cardSubtitle}>{properties}</p>
        <p className={styles.cardPrice}>{price}</p>
        <Link href={link} className={styles.exploreButton} draggable={false}>
          Explorează
        </Link>
      </div>
    </div>
  );
}

function MarketTrends() {
  return (
    <aside className={styles.trendsContainer}>
      <header className={styles.trendsHeader}>
        <h3 className={styles.trendsTitle}>Tendințe de piață</h3>
        <Link href="/market-reports" className={styles.seeAllLink}>
          Vezi raport complet
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </header>

      <div className={styles.trendsList}>
        {TRENDS_DATA.map((data) => {
          const Icon = data.icon;
          return (
            <div key={data.id} className={styles.trendItem}>
              <div className={styles.trendIconWrapper}>
                <Icon className={styles.trendIcon} />
              </div>
              <div className={styles.trendContent}>
                <span className={styles.trendLabel}>{data.label}</span>
                <div className={styles.trendValueRow}>
                  <span className={styles.trendValue}>{data.value}</span>
                  <span
                    className={
                      data.isPositive
                        ? styles.trendPositive
                        : styles.trendNegative
                    }
                  >
                    {data.trend}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
}

export default function ExploreCountrySection() {
  const scrollRef = useRef<HTMLDivElement>(null);
  const domain = useDomainContext();
  const locale = useLocale();

  // Drag to scroll logic
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleScrollLeft = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: -clientWidth, behavior: "smooth" });
    }
  };

  const handleScrollRight = () => {
    if (scrollRef.current) {
      const { clientWidth } = scrollRef.current;
      scrollRef.current.scrollBy({ left: clientWidth, behavior: "smooth" });
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!scrollRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseLeave = () => setIsDragging(false);
  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !scrollRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const searchHref = makeLocalizedPublicHref(domain, locale, "locations");

  return (
    <section className={styles.wrapper}>
      <header className={styles.mainHeader}>
        <div className={styles.titleWrapper}>
          <span className={styles.eyebrow}>Piața imobiliară în România</span>
          <h2 className={styles.mainTitle}>
            Explorează cele mai căutate
            <br />
            zone din România
          </h2>
        </div>
        <Link href={searchHref} className={styles.headerLink}>
          Vezi toate zonele
          <MdArrowForward className={styles.linkIcon} />
        </Link>
      </header>

      <div className={styles.contentFlex}>
        {/* Left Side: Cards Carousel */}
        <div className={styles.carouselWrapper}>
          <div
            className={`${styles.cardsTrack} ${isDragging ? styles.dragging : ""}`}
            ref={scrollRef}
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
          >
            {CITIES_DATA.map((data) => (
              <CityCard
                key={data.id}
                imageUrl={data.imageUrl}
                title={data.title}
                properties={data.properties}
                price={data.price}
                link={data.link}
              />
            ))}
          </div>

          <div className={styles.carouselControls}>
            <button
              className={styles.controlBtn}
              aria-label="Previous"
              onClick={handleScrollLeft}
            >
              <MdOutlineChevronLeft />
            </button>
            <button
              className={styles.controlBtn}
              aria-label="Next"
              onClick={handleScrollRight}
            >
              <MdOutlineChevronRight />
            </button>
          </div>
        </div>

        {/* Right Side: Market Trends */}
        <MarketTrends />
      </div>
    </section>
  );
}
